import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseDynamicHeightOptions {
  additionalOffset?: number;
  useDynamicViewport?: boolean;
}

interface DynamicHeightReturn {
  headerRef: React.RefObject<HTMLDivElement>;
  footerRef: React.RefObject<HTMLDivElement>;
  headerHeight: number;
  footerHeight: number;
  contentHeight: string;
  mobileViewportHeight: number;
  remeasure: () => void;
}

export const useDynamicHeight = (options: UseDynamicHeightOptions = {}): DynamicHeightReturn => {
  const { additionalOffset = 0, useDynamicViewport = true } = options;

  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(
    window.visualViewport?.height ?? window.innerHeight,
  );

  const measureElements = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
    if (footerRef.current) {
      setFooterHeight(footerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const handleViewportChange = () => {
      const newHeight = window.visualViewport?.height ?? window.innerHeight;
      setMobileViewportHeight(newHeight);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    }

    window.addEventListener('resize', handleViewportChange);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
      window.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }

    const measureHeader = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        setHeaderHeight(newHeight);
      }
    });

    resizeObserver.observe(headerRef.current);
    measureHeader();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!footerRef.current) {
      return;
    }

    const measureFooter = () => {
      if (footerRef.current) {
        setFooterHeight(footerRef.current.offsetHeight);
      }
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        setFooterHeight(newHeight);
      }
    });

    resizeObserver.observe(footerRef.current);
    measureFooter();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
      if (footerRef.current) {
        setFooterHeight(footerRef.current.offsetHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contentHeight = useMemo(() => {
    const isMobile = window.innerWidth < 1024;
    const baseHeight =
      isMobile && useDynamicViewport
        ? `${mobileViewportHeight}px`
        : useDynamicViewport
          ? '100dvh'
          : '100vh';

    const totalOffset = headerHeight + footerHeight + additionalOffset;

    return `calc(${baseHeight} - ${totalOffset}px)`;
  }, [headerHeight, footerHeight, additionalOffset, useDynamicViewport, mobileViewportHeight]);

  return {
    headerRef,
    footerRef,
    headerHeight,
    footerHeight,
    contentHeight,
    mobileViewportHeight,
    remeasure: measureElements,
  };
};
